import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/JsonLdSchema';

const sections = [
  {
    id: 'section-1', num: '1', title: 'Overview',
    content: `DPOS is a restaurant management and POS platform used by merchants (restaurants, cafés, cloud kitchens) and their staff. We generally process data on behalf of the merchant to deliver billing, reporting, and operational features.`,
    note: { title: 'Important Note (Merchant Data)', text: 'Transaction records, menu details and outlet reports belong to the merchant. We use this data only to provide and improve the Service and to meet legal obligations.' }
  },
  {
    id: 'section-2', num: '2', title: 'Information We Collect',
    bullets: [
      '**Account & Business Info:** outlet name, owner/admin details, contact email/phone, address, GST details (if provided), staff user accounts and roles.',
      '**Operational Data:** menu items, pricing, taxes, recipes (if enabled), vendors, purchase orders, staff actions in the system.',
      '**Transaction Data:** orders, bills, refunds, discounts, payment method type and status (we do not store full card details; payment processing is handled by payment partners).',
      '**Device & Log Data:** IP address, device identifiers, browser/app version, crash logs, and security logs.',
      '**Support & Communication:** messages sent via forms, email, WhatsApp, or calls and related troubleshooting details.',
    ],
    footer: '**Customer Data:** If the merchant chooses to collect customer information (name/phone/email) for loyalty or delivery, that data is controlled by the merchant. DPOS processes it to provide the feature.'
  },
  {
    id: 'section-3', num: '3', title: 'How We Use Data',
    intro: 'We use information to:',
    bullets: [
      'Provide billing, reporting, and restaurant operations features.',
      'Authenticate users, manage permissions, and protect accounts from fraud/abuse.',
      'Send service updates, invoices, onboarding, and important notices.',
      'Offer customer support, troubleshoot issues, and improve product performance.',
      'Comply with legal obligations and respond to lawful requests.',
    ],
    note: { title: 'Analytics & Improvements', text: 'We may use aggregated or de-identified data (that cannot reasonably identify a person) to understand usage patterns and improve DPOS features.' }
  },
  {
    id: 'section-4', num: '4', title: 'Sharing & Integrations',
    intro: 'We do not sell your personal information. We may share data only in the following cases:',
    bullets: [
      '**Service Providers:** hosting, storage, SMS/email delivery, support tooling, analytics—only as needed to run the Service.',
      '**Payment Partners:** when you enable payment integrations, relevant order/payment status information may be shared with the partner.',
      '**Merchant-Enabled Integrations:** third-party delivery, KDS, accounting, or other tools you connect.',
      '**Legal & Safety:** when required by law or to protect rights, safety, and security.',
    ],
    footer: 'Integration data sharing depends on the merchant\'s configuration. We recommend reviewing third-party privacy policies before enabling integrations.'
  },
  {
    id: 'section-5', num: '5', title: 'Cookies & Tracking',
    content: 'Our website may use cookies and similar technologies to remember preferences, keep you signed in, and understand how pages are used.',
    bullets: [
      '**Essential:** login/session, security.',
      '**Preferences:** theme and UI settings.',
      '**Analytics:** aggregated usage metrics (where enabled).',
    ],
    footer: 'You can control cookies through your browser settings, but disabling essential cookies may affect site functionality.'
  },
  {
    id: 'section-6', num: '6', title: 'Security & Retention',
    content: 'We implement reasonable technical and organizational measures designed to protect information from unauthorized access, loss, misuse, or alteration.',
    note: { title: 'Security Responsibilities', text: 'Merchants and staff should use strong passwords, keep devices secure, and limit access based on roles. Please notify us immediately if you suspect unauthorized use.' },
    footer: 'We retain data for as long as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements. Merchants may request export or deletion where applicable and legally permissible.'
  },
  {
    id: 'section-7', num: '7', title: 'Your Rights',
    content: 'Depending on your relationship with DPOS (merchant admin, staff user, or website visitor), you may have rights to access, correct, export, or delete information.',
    bullets: [
      'Request a copy of certain data associated with your account.',
      'Update or correct inaccurate business or account details.',
      'Request deletion of your account (subject to legal and contractual requirements).',
      'Opt out of non-essential communications (where applicable).',
    ],
    footer: 'To make a request, contact us using the details below.'
  },
  {
    id: 'section-8', num: '8', title: 'Contact Us',
    content: 'If you have questions about this Privacy Policy or our data practices, please contact:',
    contacts: [
      { label: 'Email', val: 'support@dpos.co.in', href: 'mailto:support@dpos.co.in' },
      { label: 'Phone', val: '+91 99205 93222', href: 'tel:+919920593222' },
      { label: 'Support Page', val: 'dpos.co.in/contact', href: '/contact' },
    ],
    footer: 'We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.'
  },
];

const toc = [
  { id: 'section-1', label: '1. Overview' },
  { id: 'section-2', label: '2. Information We Collect' },
  { id: 'section-3', label: '3. How We Use Data' },
  { id: 'section-4', label: '4. Sharing & Integrations' },
  { id: 'section-5', label: '5. Cookies & Tracking' },
  { id: 'section-6', label: '6. Security & Retention' },
  { id: 'section-7', label: '7. Your Rights' },
  { id: 'section-8', label: '8. Contact Us' },
];

function renderText(str) {
  return str.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

export default function Privacy() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="Privacy Policy – DPOS"
        description="Read the DPOS privacy policy to understand how we collect, use, and protect your business and customer data."
        canonical="/privacy"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Privacy Policy' },
      ]} />
      <section style={{ padding: '72px 24px 80px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '280px 1fr', gap: 64, alignItems: 'flex-start' }} className="legal-grid">

          {/* Sidebar */}
          <div style={{ position: 'sticky', top: 88 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Privacy Policy</h1>
            <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 28 }}>Last Updated: October 24, 2025</p>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Table of Contents</p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {toc.map(t => (
                <a key={t.id} href={`#${t.id}`} style={{ fontSize: 13, color: '#6b7280', padding: '6px 12px', borderRadius: 6, transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.background = '#fef2f2'; e.target.style.color = '#CC0000'; }}
                  onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#6b7280'; }}
                >{t.label}</a>
              ))}
            </nav>
            <div style={{ marginTop: 32, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 6 }}>Need Help?</p>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Questions about privacy or data handling? Reach our support team.</p>
              <Link to="/contact" style={{ fontSize: 13, fontWeight: 600, color: '#CC0000' }}>Contact Support →</Link>
            </div>
          </div>

          {/* Content */}
          <div>
            <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.8, marginBottom: 40, paddingBottom: 40, borderBottom: '1px solid #e5e7eb' }}>
              This Privacy Policy explains how DPOS ("we", "us", "our") collects, uses, shares, and protects information when you use our website, applications, and point-of-sale services (the "Service"). By using DPOS, you agree to the practices described here.
            </p>

            {sections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: 56, paddingBottom: 56, borderBottom: '1px solid #f3f4f6' }}>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 16 }}>
                  {s.num}. {s.title}
                </h2>
                {s.content && <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.8, marginBottom: 16 }}>{s.content}</p>}
                {s.intro && <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.8, marginBottom: 12 }}>{s.intro}</p>}
                {s.note && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>{s.note.title}</p>
                    <p style={{ fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>{s.note.text}</p>
                  </div>
                )}
                {s.bullets && (
                  <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {s.bullets.map((b, i) => (
                      <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, listStyle: 'disc' }}
                        dangerouslySetInnerHTML={{ __html: renderText(b) }} />
                    ))}
                  </ul>
                )}
                {s.contacts && (
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {s.contacts.map(c => (
                      <li key={c.label} style={{ fontSize: 14, color: '#374151' }}>
                        <strong>{c.label}:</strong>{' '}
                        <a href={c.href} style={{ color: '#CC0000' }}>{c.val}</a>
                      </li>
                    ))}
                  </ul>
                )}
                {s.footer && <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: renderText(s.footer) }} />}
              </div>
            ))}
          </div>
        </div>
      </section>
      <style>{`@media(max-width:900px){.legal-grid{grid-template-columns:1fr !important;}}`}</style>
    </main>
  );
}
